<script
  setup
  lang="ts"
  generic="T extends string | number | object, A extends LoaderFnArgs = LoaderFnArgs"
>
  import { computedAsync, useDebounceFn } from '@vueuse/core';
  import { computed, ref, watch } from 'vue';
  import type { HTMLAttributes, Ref } from 'vue';
  import { boolean } from 'zod';

  import { indexOf } from '@stuut/common/src/utils';
  import { Checkbox } from '@ui/components/checkbox';
  import { useLoader } from '@ui/components/combo-select/useLoader';
  import type { Loader, LoaderFnArgs } from '@ui/components/combo-select/useLoader';
  import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
  } from '@ui/components/command';
  import LoadingSpinner from '@ui/components/LoadingSpinner.vue';
  import { cn } from '@ui/utils/utils';

  export interface CommandGrouping {
    key: string;
    label: string;
  }

  interface ComboCommandProps {
    additionalOptions?: T[];
    clearable?: boolean;
    disabled?: boolean;
    disabledFn?: (option: T) => boolean;
    dropdownClass?: HTMLAttributes['class'];
    exclude?: (string | number)[];
    filter?: (searchTerm?: string) => Promise<T[]>;
    groupBy?: (item: T) => string;
    groupHeadingClass?: HTMLAttributes['class'];
    groups?: CommandGrouping[];
    inPlaceSearch?: boolean;
    keepSelectedInPlace?: boolean;
    lazyLoad?: boolean;
    loader?: Loader<T, A>;
    loadOnOpen?: boolean;
    multiple?: boolean;
    noSave?: boolean;
    optionClass?: HTMLAttributes['class'];
    optionIdentifier?: string;
    optionLabel?: string;
    options?: T[];
    placeholder?: string;
    search?: string;
    searchable?: boolean;
    searchAttrs?: string[];
    searchPlaceholder?: string;
    sectionHeading?: string;
    showCheckIcon?: boolean;
    showSearchIcon?: boolean;
    showSelectedAtTop?: boolean;
  }

  const props = withDefaults(defineProps<ComboCommandProps>(), {
    keepSelectedInPlace: false,
    options: () => [],
    placeholder: 'Select Value',
    searchPlaceholder: 'Search...',
    showSelectedAtTop: true,
  });

  const emits = defineEmits<{ (e: 'select', option: T) }>();

  const modelValue = defineModel<T | T[] | null>({ default: null });

  const open = defineModel<boolean>('open', { default: false });

  const createDefaultLoader = () => {
    return useLoader({
      load: () => Promise.resolve(props.options),
    });
  };

  const { data, initialData, initialDataLoaded, load, loading } =
    props.loader || createDefaultLoader();

  const initialOptions = ref<T[]>(props.options || []) as Ref<T[]>;

  watch(
    () => props.options,
    (newOptions) => {
      initialOptions.value = newOptions || [];
    },
    { immediate: true },
  );

  const currentOptions = computed<T[]>(() => {
    return data.value || [];
  });

  const searchString = ref<string>();

  const resolvedGroups = computed<CommandGrouping[]>(() => {
    if (props.groups?.length) return props.groups;

    return [
      {
        key: '',
        label: props.sectionHeading || '',
      },
    ];
  });

  const getGroupKey = (option: T): string => {
    return props.groupBy && props.groups?.length ? props.groupBy(option) : '';
  };

  const internalValue = computed<T[]>(() =>
    modelValue.value
      ? Array.isArray(modelValue.value)
        ? modelValue.value
        : [modelValue.value]
      : [],
  );

  const initiallySelected = ref<T[]>(internalValue.value.slice());

  const valueKeys = computed<Set<number | string>>(
    () => new Set([...internalValue.value.map((v) => getOptionValue(v))]),
  );

  const compare = (a: T, b: T): boolean => {
    return getOptionValue(a) === getOptionValue(b);
  };

  const indexOfSelected = (option: T): number => {
    return indexOf(internalValue.value, (v: T) => compare(v, option));
  };

  const isSelected = (option: T): boolean => {
    return valueKeys.value.has(getOptionValue(option));
  };

  const getOptionValue = (option: T): number | string => {
    return props.optionIdentifier ? option[props.optionIdentifier] : (option as number | string);
  };

  const select = (option: T) => {
    emits('select', option);
    if (props.noSave) {
      open.value = false;
      return;
    }

    const idx = indexOfSelected(option);
    if (props.multiple) {
      const internalCopy = internalValue.value.slice();
      if (idx === -1) {
        internalCopy.push(option);
      } else {
        internalCopy.splice(idx, 1);
      }

      modelValue.value = internalCopy.length ? internalCopy : null;
    } else {
      open.value = false;
      if (modelValue.value && getOptionValue(option) === getOptionValue(modelValue.value as T)) {
        if (props.clearable) {
          modelValue.value = null;
        }
      } else {
        modelValue.value = option;
      }
    }
  };

  const filterOptions = (options: T[], searchTerm?: string) => {
    if (!searchTerm?.trim()) {
      return options;
    }

    const lowerSearchTerm = searchTerm.toLowerCase();
    const attrKeys = new Set(props.searchAttrs || []);
    if (props.optionIdentifier) {
      attrKeys.add(props.optionIdentifier);
    }
    return options.filter(
      // @ts-ignore
      (i) => {
        const attrs: string[] = [];
        if (i instanceof String) {
          attrs.push(i.toLowerCase());
        } else {
          attrs.push(
            ...Array.from(attrKeys.values())
              .map((key) => i[key])
              .filter((v) => boolean(v)),
          );
        }

        return attrs.some((a) => a && a.toLowerCase().includes(lowerSearchTerm));
      },
    );
  };

  const visibleOptions = computedAsync<T[]>((): T[] => {
    if (loading.value) return [];

    // Only filter out selected items if NOT keeping them in place
    const initialSet = props.keepSelectedInPlace
      ? new Set<string | number>()
      : new Set([
          // @ts-ignore
          ...initiallySelected.value.map((option: T) => getOptionValue(option)),
        ]);

    const baseOptions = currentOptions.value.slice();
    if (props.additionalOptions) {
      baseOptions.unshift(...filterOptions(props.additionalOptions, searchString.value));
    }

    return baseOptions.filter((option) => {
      const val = getOptionValue(option);
      return !initialSet.has(val) && !props.exclude?.includes(val);
    });
  }, []);

  const debouncedSearch = useDebounceFn(async (searchTerm?: string) => {
    try {
      await load({ search: searchTerm } as A);
    } finally {
    }
  }, 300);

  let clearSearchTimeout: ReturnType<typeof setTimeout> | null = null;

  watch(
    open,
    (isOpen) => {
      if (!isOpen) {
        clearSearchTimeout = setTimeout(() => {
          initiallySelected.value = internalValue.value.slice();
          searchString.value = '';
          data.value = filterOptions(initialData.value, searchString.value);
          clearSearchTimeout = null;
        }, 250);
      } else {
        // Cancel any pending search clear since we're reopening
        if (clearSearchTimeout) {
          clearTimeout(clearSearchTimeout);
          clearSearchTimeout = null;
        }

        if (props.search) {
          searchString.value = props.search;
        }

        initiallySelected.value = internalValue.value.slice();
      }

      if (isOpen && (!initialDataLoaded.value || props.loadOnOpen) && !loading.value) {
        // When loadOnOpen is true, pass empty string instead of undefined to avoid cache
        const searchValue =
          props.loadOnOpen && searchString.value === undefined ? '' : searchString.value;
        load({ search: searchValue } as A);
      }
    },
    { immediate: true },
  );

  watch(searchString, (newSearchVal, oldSearchVal) => {
    if (props.loader) {
      if (!newSearchVal?.length && !oldSearchVal?.length) {
        load();
      } else if (props.inPlaceSearch) {
        data.value = filterOptions(initialData.value, newSearchVal);
      } else {
        debouncedSearch(newSearchVal);
      }
    } else {
      data.value = filterOptions(initialOptions.value, newSearchVal);
    }
  });

  // Sync external search prop to internal searchString when it changes
  watch(
    () => props.search,
    (newSearch) => {
      // Sync external search to internal when:
      // 1. Dropdown is open and search changed
      // 2. Search value arrives and it's different from current
      if (open.value && newSearch !== undefined && newSearch !== searchString.value) {
        searchString.value = newSearch || '';
      }
    },
    { immediate: true },
  );

  const closeSelect = () => {
    open.value = false;
  };
</script>

<template>
  <Command :multiple>
    <!-- Server-side search: plain input (doesn't trigger Command's client-side filter) -->
    <template v-if="searchable && loader">
      <div data-slot="command-input-wrapper" class="flex h-9 items-center gap-2 border-b px-3">
        <i v-if="showSearchIcon" class="fa fa-search text-xs opacity-50 size-4 pt-0.5" />
        <input
          v-model="searchString"
          :placeholder="searchPlaceholder"
          class="placeholder:text-muted-foreground flex h-9 py-1.5 w-full rounded-sm bg-transparent text-xs outline-hidden disabled:cursor-not-allowed disabled:opacity-50"
          autofocus
        />
      </div>
    </template>

    <!-- Client-side filtering: CommandInput (integrates with Command's filter) -->
    <CommandInput
      v-else-if="searchable"
      :hide-search-icon="!showSearchIcon"
      :placeholder="searchPlaceholder"
    />

    <div v-if="$slots.fixed" class="w-full group px-1 pt-1" role="group">
      <slot name="fixed" v-bind="{ closeSelect }" />
    </div>

    <CommandList :class="dropdownClass">
      <!--      <CommandGroup v-if="$slots.fixed" class="flex flex-col overflow-y-auto"> -->
      <!--        <slot name="fixed"> </slot> -->
      <!--      </CommandGroup> -->

      <!-- Empty state for server-side search: explicit check -->
      <!--      @vue-ignore -->
      <div
        v-if="
          loader &&
          initialDataLoaded &&
          !loading &&
          !visibleOptions.length &&
          !filterOptions(initiallySelected, searchString).length
        "
        class="py-6 text-center text-xs font-medium"
      >
        <slot name="empty">
          <span> No items found. </span>
        </slot>
      </div>

      <!-- Empty state for client-side: CommandEmpty handles visibility -->
      <CommandEmpty
        v-else-if="initialDataLoaded && !loading"
        :force-render="!searchable && !visibleOptions.length"
      >
        <slot name="empty">
          <span> No items found. </span>
        </slot>
      </CommandEmpty>

      <CommandGroup
        v-if="showSelectedAtTop && initialDataLoaded && initiallySelected.length && !loading"
        class="border-b"
      >
        <!-- @vue-ignore -->
        <CommandItem
          v-for="(option, index) in filterOptions(initiallySelected, searchString)"
          :key="optionIdentifier ? option[optionIdentifier] : option"
          :value="option"
          :class="cn('flex text-xs h-9 gap-6 items-center justify-between', optionClass)"
          @select="select(option)"
        >
          <div class="flex flex-row truncate gap-2 items-center">
            <Checkbox v-if="multiple && !showCheckIcon" :model-value="isSelected(option)" />

            <slot name="selected" :option="option" :value="option" :index="index">
              <span>
                {{ optionLabel ? option[optionLabel] : getOptionValue(option) }}
              </span>
            </slot>
          </div>

          <i v-if="!multiple || showCheckIcon" class="fas fa-check text-success" />
        </CommandItem>
      </CommandGroup>

      <div
        v-if="!initialDataLoaded || loading"
        class="text-xs flex items-center justify-center py-6 px-3"
      >
        <LoadingSpinner />
      </div>

      <template v-if="!loading && visibleOptions.length">
        <CommandGroup
          v-for="group in resolvedGroups"
          :key="group.key"
          class="flex flex-col overflow-y-auto"
          :heading="group.label"
          :heading-class="groupHeadingClass"
        >
          <CommandItem
            v-for="(option, index) in visibleOptions.filter(
              (opt) => getGroupKey(opt) === group.key,
            )"
            :key="optionIdentifier ? option[optionIdentifier] : option"
            :value="option"
            :class="
              cn('flex text-xs h-9', optionClass, {
                'justify-between': showCheckIcon,
                'bg-muted': showCheckIcon && isSelected(option),
              })
            "
            :disabled="disabledFn && disabledFn(option)"
            @select="select(option)"
          >
            <Checkbox v-if="multiple && !showCheckIcon" :model-value="isSelected(option)" />

            <slot name="option" :option="option" :index="index">
              <span>
                {{
                  optionLabel
                    ? option[optionLabel]
                    : optionIdentifier
                      ? option[optionIdentifier]
                      : option
                }}
              </span>
            </slot>

            <i v-if="showCheckIcon && isSelected(option)" class="fas fa-check text-success" />
          </CommandItem>
        </CommandGroup>
      </template>
    </CommandList>

    <div v-if="$slots.fixedBottom" class="w-full group p-1 border-t" role="group">
      <slot name="fixedBottom" v-bind="{ closeSelect }" />
    </div>
  </Command>
</template>

<style scoped></style>
